import { ReactNode } from 'react';

// Layout areas - core areas plus additional layout components
export enum LayoutArea {
  HEADER = 'header',
  LEFT_SIDEBAR = 'left-sidebar',
  CONTENT = 'content',
  RIGHT_SIDEBAR = 'right-sidebar',
  FOOTER = 'footer',
  // Additional layout components
  BOTTOM_PANEL = 'bottom-panel',
  TOP_PANEL = 'top-panel',
  TABS = 'tabs',
  SECONDARY_TABS = 'secondary-tabs',
  ACTION_BUTTONS = 'action-buttons',
  BREADCRUMB = 'breadcrumb',
  SUBHEADER = 'subheader',
}

// Deprecated areas (for backward compatibility and migration)
// These should be handled as sub-components within the 5 core areas
export enum DeprecatedLayoutArea {
  BOTTOM_PANEL = 'bottom-panel', // Use FOOTER instead
  TOP_PANEL = 'top-panel', // Use HEADER instead
  TABS = 'tabs', // Handle as sub-component of HEADER
  SECONDARY_TABS = 'secondary-tabs', // Handle as sub-component of HEADER
  ACTION_BUTTONS = 'action-buttons', // Handle as sub-component of HEADER
  BREADCRUMB = 'breadcrumb', // Handle as sub-component of HEADER
  SUBHEADER = 'subheader', // Handle as sub-component of HEADER
}

// Legacy alias for backward compatibility
export enum PanelPosition {
  LEFT = LayoutArea.LEFT_SIDEBAR,
  RIGHT = LayoutArea.RIGHT_SIDEBAR,
  BOTTOM = LayoutArea.BOTTOM_PANEL,
  TOP = LayoutArea.TOP_PANEL,
}

// Modal and drawer types
export enum ModalType {
  DIALOG = 'dialog',
  FULLSCREEN = 'fullscreen',
  CONFIRM = 'confirm',
}

export enum SidebarPosition {
  LEFT = 'left',
  RIGHT = 'right',
}

// Responsive behavior modes
export enum ResponsiveMode {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

// Panel sizing units
export type SizeUnit = 'percent' | 'pixels' | 'auto';

// Panel size constraint - can be percentage or pixels
export interface SizeConstraint {
  value: number;
  unit: SizeUnit;
}

// Panel configuration interface with hybrid sizing support
export interface PanelConfig {
  defaultSize: number | SizeConstraint;
  minSize: number | SizeConstraint;
  maxSize: number | SizeConstraint;
  collapsible: boolean;
  resizable?: boolean;
  // Pixel-based constraints for content-aware sizing
  minPixels?: number;
  maxPixels?: number;
  // Auto-sizing behavior
  autoSize?: 'content' | 'fixed' | 'flexible';
  // Collapse behavior
  collapseSize?: number; // Size when collapsed (in pixels or percent)
  collapseThreshold?: number; // Size threshold to trigger auto-collapse
}

// Responsive panel configuration
export interface ResponsivePanelConfig {
  direction?: 'horizontal' | 'vertical';
  stackPanels?: boolean;
  hideSecondaryPanels?: boolean;
  collapsibleByDefault?: boolean;
  showAllPanels?: boolean;
  // Added missing properties from constants.ts
  sidebarMode?: 'overlay' | 'stack' | 'hidden';
  compactSpacing?: boolean;
  collapsePanels?: LayoutArea[];
  panelOverrides?: Partial<Record<LayoutArea, Partial<PanelConfig>>>;
}

// Modal configuration
export interface ModalConfig {
  type: ModalType;
  backdrop?: boolean;
  closable?: boolean;
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  onClose?: () => void;
}

// Panel state interface
export interface PanelState {
  content: ReactNode;
  isVisible: boolean;
  size?: number;
  isCollapsed?: boolean;
  metadata?: {
    contentId?: string;
    activeEntryId?: string;
    conflictEntries?: {
      id: string;
      content: ReactNode;
      priority: number;
      timestamp: number;
      source?: string;
      metadata?: Record<string, unknown>;
    }[];
    conflictResolution?: {
      strategy: 'replace' | 'tabs' | 'stack' | 'queue';
      reason: string;
    };
    source?: string;
    priority?: number;
    [key: string]: unknown;
  };
}

// Auto layout context interface
export interface AutoPanelContextType {
  // Panel management
  setPanel: (
    area: LayoutArea,
    content: ReactNode,
    config?: Partial<PanelConfig> & {
      contentId?: string;
      priority?: number;
      source?: string;
      forceReplace?: boolean;
    },
  ) => void;
  clearPanel: (area: LayoutArea) => void;
  togglePanel: (area: LayoutArea) => void;
  collapsePanel: (area: LayoutArea) => void;
  expandPanel: (area: LayoutArea) => void;
  resizePanel: (area: LayoutArea, size: number) => void;

  // Panel state queries
  isPanelVisible: (area: LayoutArea) => boolean;
  isPanelCollapsed: (area: LayoutArea) => boolean;
  getPanelSize: (area: LayoutArea) => number | undefined;

  // Modal and drawer management
  showModal: (content: ReactNode, config?: ModalConfig) => void;
  hideModal: () => void;
  showDrawer: (position: SidebarPosition, content: ReactNode) => void;
  hideDrawer: () => void;

  // Layout state
  activePanels: LayoutArea[];
  panelStates: Map<LayoutArea, PanelState>;
}

// Main AutoPanelLayout props
export interface AutoPanelLayoutProps {
  children: ReactNode;
  autoSaveId?: string;
  direction?: 'horizontal' | 'vertical';
  className?: string;
  responsive?: Partial<Record<ResponsiveMode, ResponsivePanelConfig>>;
  panelConfig?: Partial<Record<LayoutArea, Partial<PanelConfig>>>;

  // Enhanced callbacks from react-resizable-panels
  onLayout?: (sizes: number[]) => void;
  onPanelChange?: (area: LayoutArea, isVisible: boolean, size?: number) => void;

  // Panel interaction handlers
  resizeHandlers?: PanelResizeHandlers;

  // Pixel-based sizing options
  enablePixelSizing?: boolean;
  viewportPadding?: number; // Reserve space for scrollbars, etc.

  // Tab-based layout props (replaces PageWithTabsEnhanced functionality)
  tabs?: ReactNode;
  secondaryTabs?: ReactNode;
  actionButtons?: ReactNode;
  header?: ReactNode;
  breadcrumb?: ReactNode;
  subheader?: ReactNode;
  footer?: ReactNode;

  // Mobile configuration for tab layouts
  mobileConfig?: {
    sidebarMode?: 'overlay' | 'stack' | 'hidden';
    collapseTabs?: boolean;
    compactSpacing?: boolean;
    hideActionsOnMobile?: boolean;
  };

  // Layout configuration
  layoutConfig?: {
    gridTemplateAreas?: string;
    gridTemplateColumns?: string;
    gridTemplateRows?: string;
    noSpacing?: boolean;
  };
}

// Content validation function type
export type ContentValidationFn = (content: ReactNode) => boolean;

// Panel resize handlers
export interface PanelResizeHandlers {
  onDragStart?: (area: LayoutArea, initialSize: number) => void;
  onDragEnd?: (area: LayoutArea, finalSize: number) => void;
  onResize?: (area: LayoutArea, currentSize: number, sizeConstraints: SizeConstraints) => void;
  onCollapse?: (area: LayoutArea, isCollapsed: boolean) => void;
  onAutoResize?: (area: LayoutArea, reason: 'viewport' | 'content' | 'constraint', newSize: number) => void;
}

// Size constraints calculated from config and viewport
export interface SizeConstraints {
  minPercent: number;
  maxPercent: number;
  minPixels: number;
  maxPixels: number;
  preferredPixels?: number;
  canCollapse: boolean;
  collapseThreshold?: number;
}

// Dynamic sizing calculation result
export interface DynamicSizeResult {
  size: number; // Final size in percentage
  sizePixels: number; // Size in pixels
  constraints: SizeConstraints;
  reason: string;
  isCollapsed: boolean;
  adaptations: string[]; // List of adaptations made
}

// Panel group variants for different use cases
export enum PanelGroupVariant {
  PATIENT_EDIT = 'patient-edit',
  FILE_VIEWER = 'file-viewer',
  DASHBOARD = 'dashboard',
  ADMIN = 'admin',
}

// Panel states map type
export type PanelStatesMap = Map<LayoutArea, PanelState>;

// Conflict resolution interface (re-exported from ContentConflictResolver)
export interface ConflictResolution {
  strategy: 'replace' | 'tabs' | 'stack' | 'queue';
  activeEntry?: string;
  entryCount?: number;
  timestamp?: number;
}
